// Configuración › Conexiones › Navegador de Mercado Libre (2026-10-08).
// Tres tarjetas (una por empresa) con el estado de la sesión WEB de Mercado Libre del navegador del
// servidor + pantalla remota para que el DUEÑO inicie sesión (usuario, contraseña y código de dos
// pasos). Nada de lo que se escribe se guarda en el sistema.
import { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, RefreshCw, MonitorSmartphone, X, CheckCircle, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

interface Account {
  seller_id: number;
  name: string;
  logged_in: boolean;
  session?: 'ok' | 'unconfirmed' | 'none';
  nickname?: string | null;
  cause?: string | null;
  last_check_at?: string | null;
  last_ok_at?: string | null;
  last_error?: string | null;
  url?: string | null;
}

interface BrowserStatus {
  configured: boolean;
  check_every_min?: number;
  accounts: Account[];
}

const NAMES: Record<number, string> = { 1: 'F5TECHNOLOGY', 6: 'TECNO4USACORP', 8: 'SELLING2GO' };
const LOGIN_URL = 'https://global-selling.mercadolibre.com/';
const HOME_URL = 'https://www.mercadolibre.com/';

// Misma lista que el servicio (el servicio vuelve a validar).
const HOST_RULES = [
  /^([a-z0-9-]+\.)*mercadolibre\.com$/,
  /^([a-z0-9-]+\.)*mercadolibre\.com\.(ar|mx|co|uy|pe)$/,
  /^([a-z0-9-]+\.)*mercadolibre\.cl$/,
  /^([a-z0-9-]+\.)*mercadolivre\.com\.br$/,
  /^([a-z0-9-]+\.)*mercadopago\.com(\.(ar|mx|co|br))?$/,
  /^([a-z0-9-]+\.)*mercadopago\.cl$/,
];
function allowedUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' || u.username || u.password || u.port) return false;
    return HOST_RULES.some((r) => r.test(u.hostname.toLowerCase()));
  } catch {
    return false;
  }
}

const fmtAgo = (iso?: string | null): string => {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
};

const CAUSE_TEXT: Record<string, string> = {
  login: 'Mercado Libre pide iniciar sesión',
  '2fa': 'Falta el código de dos pasos',
  captcha: 'Mercado Libre pide resolver un captcha',
  blocked: 'Mercado Libre bloqueó la página para este navegador',
  error: 'Mercado Libre respondió con un error',
  country_selector: 'La pestaña quedó en el selector de país',
  unknown: 'No se pudo saber qué muestra la pestaña',
};

const REMOTE_W = 1280;
const REMOTE_H = 800;
const REMOTE_KEYS = new Set(['Enter', 'Backspace', 'Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Escape', 'Delete', 'Home', 'End', 'PageDown', 'PageUp']);

function RemoteScreen({ sellerId, onClose }: { sellerId: number; onClose: () => void }) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [src, setSrc] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [addr, setAddr] = useState<string>('');
  const busy = useRef(false);

  // Cola de envío: cada pedido espera al anterior (las teclas llegan en orden).
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const doneSent = useRef(false);
  const remote = useCallback(
    (action: string, payload: Record<string, unknown> = {}) => {
      const job = chain.current.then(() =>
        api.post<Record<string, unknown>>('/settings/ml-browser/remote', { seller_id: sellerId, action, ...payload })
      );
      chain.current = job.catch(() => undefined);
      return job;
    },
    [sellerId]
  );
  // Cerrar la ventana libera la pantalla en el servidor (manda "done" si no se mandó ya).
  const close = useCallback(() => {
    if (!doneSent.current) { doneSent.current = true; remote('done').catch(() => undefined); }
    onClose();
  }, [remote, onClose]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (busy.current || !alive) return;
      busy.current = true;
      try {
        const blob = await api.getBlob(`/settings/ml-browser/shot?seller_id=${sellerId}&fmt=jpeg&n=${Date.now()}`);
        if (blob && alive) {
          const url = URL.createObjectURL(blob);
          setSrc((old) => { if (old) URL.revokeObjectURL(old); return url; });
        }
      } catch { /* la próxima captura lo intenta de nuevo */ }
      busy.current = false;
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [sellerId]);

  const go = async (url: string) => {
    if (!allowedUrl(url)) { setNote('❌ Solo se puede abrir Mercado Libre, Mercado Livre o Mercado Pago (https).'); return; }
    setNote('');
    const res = await remote('navigate', { url });
    if (!res.success) setNote(`❌ ${res.error || 'No se pudo abrir'}`);
  };

  const onClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    remote('click', { x: ((e.clientX - r.left) * REMOTE_W) / r.width, y: ((e.clientY - r.top) * REMOTE_H) / r.height });
    imgRef.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent<HTMLImageElement>) => {
    e.preventDefault();
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) remote('type', { text: e.key });
    else if (REMOTE_KEYS.has(e.key)) remote('key', { key: e.key });
  };
  const onPaste = (e: React.ClipboardEvent<HTMLImageElement>) => {
    e.preventDefault();
    const s = e.clipboardData.getData('text');
    if (s) remote('type', { text: s });
  };
  const onWheel = (e: React.WheelEvent<HTMLImageElement>) => { remote('scroll', { dy: e.deltaY }); };

  const finish = async () => {
    setNote('Verificando la sesión…');
    doneSent.current = true;
    const res = await remote('done');
    const s = res.data as unknown as Account | undefined;
    setNote(
      s?.logged_in
        ? `✅ Sesión iniciada${s.nickname ? ` (${s.nickname})` : ''}`
        : s?.session === 'unconfirmed'
          ? '⚠️ Sin confirmar: no se ve el pedido de inicio de sesión, pero tampoco el nombre de la cuenta'
          : `❌ Todavía sin sesión${s?.last_error ? `: ${s.last_error}` : ''}`
    );
    if (s?.logged_in) setTimeout(onClose, 1500);
  };

  const btn = 'px-2.5 py-1 rounded-md bg-[var(--color-surface-hover)] text-xs text-[var(--color-text)] hover:opacity-80';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-auto p-4" onClick={close}>
      <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-[var(--color-border)] flex-wrap">
          <MonitorSmartphone className="w-4 h-4 text-[var(--color-primary)]" />
          <span className="text-sm font-medium text-[var(--color-text)]">Navegador del servidor — Mercado Libre de {NAMES[sellerId] ?? sellerId}</span>
          <div className="flex-1" />
          <button className={btn} onClick={() => go(LOGIN_URL)}>Ir a iniciar sesión</button>
          <button className={btn} onClick={() => go(HOME_URL)}>Ir a mercadolibre.com</button>
          <button className={btn} onClick={() => remote('back')} aria-label="Atrás"><ArrowLeft className="w-3 h-3 inline" /> Atrás</button>
          <button className={btn} onClick={() => remote('key', { key: 'Enter' })}>Enter</button>
          <button className={btn} onClick={() => remote('key', { key: 'Backspace' })}>Borrar</button>
          <button className={btn} onClick={() => remote('key', { key: 'Tab' })}>Tab</button>
          <button className="px-3 py-1 rounded-md bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90" onClick={finish}>Listo, verificar</button>
          <button className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]" onClick={close} aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </div>
        <form className="flex gap-2 px-3 pt-3" onSubmit={(e) => { e.preventDefault(); go(addr.trim()); }}>
          <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Dirección (solo Mercado Libre, Mercado Livre o Mercado Pago), por ejemplo https://www.mercadopago.com.ar/activities"
            className="flex-1 px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)]" />
          <button type="submit" className={btn}>Abrir</button>
        </form>
        <div className="p-3">
          {src ? (
            <img
              ref={imgRef}
              src={src}
              alt="Pantalla del navegador del servidor"
              tabIndex={0}
              className="w-full rounded-lg border-2 border-[var(--color-border)] focus:border-[var(--color-primary)] outline-none cursor-crosshair select-none"
              onClick={onClick}
              onKeyDown={onKey}
              onPaste={onPaste}
              onWheel={onWheel}
              draggable={false}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-[var(--color-text-muted)]"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Cargando la pantalla…</div>
          )}
          <p className="text-xs text-[var(--color-text-muted)] mt-2">
            Iniciá sesión vos (usuario, contraseña y código de dos pasos). La sesión queda guardada en el servidor; nadie ve lo que escribís.
            Hacé clic en la imagen donde quieras escribir y escribí con tu teclado. Al terminar, tocá “Listo, verificar”.
          </p>
          {note && <p className="text-sm mt-2 text-[var(--color-text)]">{note}</p>}
        </div>
      </div>
    </div>
  );
}

function AccountCard({ acc, configured, reachable, onCheck, onOpen, busy }: {
  acc: Account; configured: boolean; reachable: boolean; busy: boolean; onCheck: () => void; onOpen: () => void;
}) {
  const estado = !configured || !reachable
    ? { label: !configured ? 'Sin respuesta (no configurado en este servidor)' : 'Sin respuesta', color: 'var(--color-text-muted)', Icon: AlertTriangle }
    : acc.logged_in
      ? { label: `Iniciada${acc.nickname ? ` · ${acc.nickname}` : ''}`, color: 'var(--color-success, #16a34a)', Icon: CheckCircle }
      : acc.session === 'unconfirmed'
        ? { label: 'Sin confirmar · no se pide iniciar sesión, pero no se ve el nombre de la cuenta', color: '#d97706', Icon: AlertTriangle }
        : { label: `Sin sesión${acc.cause && CAUSE_TEXT[acc.cause] ? ` · ${CAUSE_TEXT[acc.cause]}` : acc.last_error ? ` · ${acc.last_error}` : ''}`, color: '#dc2626', Icon: XCircle };
  return (
    <div className="rounded-md border border-[var(--color-border)] p-3 bg-[var(--color-bg)] flex flex-col gap-2">
      <div className="text-sm font-medium text-[var(--color-text)]">{acc.name || NAMES[acc.seller_id]}</div>
      <div className="text-xs" style={{ color: estado.color }}>
        <estado.Icon className="w-3 h-3 inline mr-1 -mt-0.5" />{estado.label}
      </div>
      {configured && reachable && (
        <div className="text-xs text-[var(--color-text-muted)]">Último chequeo {fmtAgo(acc.last_check_at)} · última vez con sesión {fmtAgo(acc.last_ok_at)}</div>
      )}
      <Can cap="settings.ml_browser">
        <div className="flex items-center gap-2 flex-wrap">
          <button disabled={busy || !configured || !reachable} onClick={onCheck} className="px-2.5 py-1 rounded-lg bg-[var(--color-surface-hover)] text-[var(--color-text)] text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Volver a comprobar
          </button>
          <button disabled={!configured || !reachable} onClick={onOpen} className="px-2.5 py-1 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
            <MonitorSmartphone size={12} /> Abrir pantalla
          </button>
        </div>
      </Can>
    </div>
  );
}

export function MlBrowserPanel() {
  const [st, setSt] = useState<BrowserStatus | null>(null);
  const [reachable, setReachable] = useState(true);
  const [err, setErr] = useState<string>('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [remoteId, setRemoteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<BrowserStatus>('/settings/ml-browser/status');
    if (res.success && res.data) { setSt(res.data as BrowserStatus); setReachable(true); setErr(''); }
    else { setReachable(false); setErr(res.error || 'El servicio no respondió'); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const check = async (id: number) => {
    setBusyId(id);
    try {
      const res = await api.post<Account>('/settings/ml-browser/check', { seller_id: id });
      await load();
      if (!res.success) setErr(res.error || 'No se pudo comprobar');
      else if ((res.data as Account & { postponed?: boolean })?.postponed)
        setErr('La pantalla de esa cuenta está en uso: la comprobación se hace al terminar.');
    } finally { setBusyId(null); }
  };

  // Solo las cuentas que devuelve el servidor (ya filtradas por alcance): sin valor de respaldo.
  const accounts: Account[] = st?.accounts ?? [];
  const configured = st ? st.configured : true;

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] space-y-3">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-[var(--color-primary)]" />
        <div>
          <div className="text-sm font-medium text-[var(--color-text)]">Navegador de Mercado Libre (sesión web de cada empresa)</div>
          <div className="text-xs text-[var(--color-text-muted)]">
            Iniciá sesión vos (usuario, contraseña y código de dos pasos). La sesión queda guardada en el servidor; nadie ve lo que escribís.
            {st?.check_every_min ? ` Se revisa cada ${st.check_every_min} min.` : ''}
          </div>
          {err && <div className="text-xs text-[#dc2626] mt-0.5">{err}</div>}
        </div>
      </div>
      {st && accounts.length === 0 && (
        <div className="text-xs text-[var(--color-text-muted)]">No hay cuentas de Mercado Libre dentro de tu alcance.</div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        {accounts.map((a) => (
          <AccountCard key={a.seller_id} acc={a} configured={configured} reachable={reachable} busy={busyId === a.seller_id}
            onCheck={() => check(a.seller_id)} onOpen={() => setRemoteId(a.seller_id)} />
        ))}
      </div>
      {remoteId !== null && <RemoteScreen sellerId={remoteId} onClose={() => { setRemoteId(null); load(); }} />}
    </div>
  );
}
